/*
* Various form utilities & helpers
*/

import * as _ from 'lodash';
import moment from 'moment';
import createFilterOptions from 'react-select-fast-filter-options';

import * as AppUtilities from './apputilities';
import AppConfiguration from '../config/configuration';
import { Errors } from './errors';

class SelectOptionsConfig {
    public extended: Array<any>;
    public options: Array<any>;
    public filter: any;
}

export class FormUtilities {
    public static ModuleName = 'FormUtilities';

    // Common form functionality/utilities
    public static extractValueFromString(str: any, options: Array<any> = []): number {
        // 20200202 extractValueFromString refactored from src/inventory/detail.tsx
        if (!String(str) && !_.size(options)) { return parseFloat('0'); }

        let strVal: any = '0';
        if (String(str)) {
            strVal = String(str);
        } else if (_.size(options)) {
            strVal = String(_.first(options));
        }

        const matches = strVal.match(/(?:\d{1,3}(?:,\d{3})*|\d+)(?:\.\d+)?/g);
        return (matches && matches.length) ? parseFloat(matches[0].replace(/,/g, '')) : 0.0;
    }

    public static getFormChanges(frmEvent: any, frmInputElement: any, stateFormChanges: any, origFormDataCache: any): any {
        // 20200202 getFormChanges() originally refactored from From src/inventory/detail.tsx
        let result: any = _.assign({}, {
            applyChanges: false,
            formChanges: {},
            formChangesResult: {},
        });

        const msgPrefix = `FormUtilities::getFormChanges`;

        let fieldName = '';
        let newValue;

        let formChangesCopy = _.cloneDeep(stateFormChanges); // 20200202 this.state.formChanges);

        if (_.get(frmEvent, 'target.props.name')) {
            // Kendo form element
            if (_.has(frmEvent, 'target.combobox')) {
                // combobox
                fieldName = _.get(frmEvent, 'target.props.name', '');
                newValue = _.get(frmEvent, 'target.value');
            } else {
                // assume textbox
                fieldName = _.get(frmEvent, 'target.props.name', '');
                newValue = _.get(frmEvent, 'value');
            }
        } else {
            const eventTargetType = _.get(frmEvent, 'target.type', '');

            switch (eventTargetType) {
                case 'textarea':
                case 'text':
                    fieldName = _.get(frmEvent, 'target.name', '');
                    newValue = _.get(frmEvent, 'target.value');
                    break;

                case 'kendo-dropdownlist':
                    fieldName = _.get(frmEvent, 'target.name', '');
                    newValue = _.get(frmEvent, 'target.value', '');
                    break;

                case 'virtualized-select':
                    fieldName = _.get(frmEvent, 'target.name', '');
                    newValue = _.get(frmInputElement, 'value');
                    break;

                case 'delete-button':
                    fieldName = _.get(frmEvent, 'target.name', ''); // reportNotesDelete
                    newValue = _.get(frmEvent, 'target.value');
                    break;

                default:
                    // console.log(`*** ${msgPrefix} switch hit default for eventTargetType "${eventTargetType}"`);
                    break;
            }
        }

        // console.log(`${msgPrefix} continuing fieldName ${fieldName} newValue ${newValue}`, { frmEvent, frmInputElement, stateFormChanges, origFormDataCache });

        if (!fieldName) { return result; }

        let applyChange = false;
        let origVal = _.get(origFormDataCache, fieldName); // 20200202 this.originalFormDataCache, fieldName);
        let formChangesResult = {};
        if (_.isArray(origVal)) {
            if (_.isArray(newValue)) {
                if (!_.isEqual(origVal, newValue)) {
                    _.set(formChangesCopy, fieldName, _.cloneDeep(newValue));
                    _.set(formChangesResult, fieldName, _.cloneDeep(newValue));
                    applyChange = true;
                } else {
                    applyChange = false;
                }
            } else {
                if (_.includes(origVal, newValue)) {
                    // console.log(`original array already contained val ${newValue} origVal`, origVal);
                    applyChange = false;
                } else {
                    const formChangesArr = _.get(formChangesCopy, fieldName, []);
                    // console.log(`${fieldName} formChangesArr`, formChangesArr);
                    if (_.includes(formChangesArr, newValue)) {
                        // console.log(`formChangesArr field ${fieldName} array already contains val ${newValue} formChangesArr`, formChangesArr);
                        applyChange = false;
                    } else {
                        formChangesArr.push(newValue);
                        _.set(formChangesCopy, fieldName, _.cloneDeep(formChangesArr));
                        _.set(formChangesResult, fieldName, _.cloneDeep(formChangesArr));
                        applyChange = true;
                        // console.log(`formChangesArr field ${fieldName} array DID NOT have val ${newValue}, applying change`, formChangesArr);
                    }
                }
            }
        } else {
            if (String(newValue) !== String(origVal)) {
                // New value
                _.set(formChangesCopy, fieldName, newValue);
                _.set(formChangesResult, fieldName, newValue);
                applyChange = true;

            } else {
                // Same as orig val
                if (_.has(formChangesCopy, fieldName)) {
                    _.unset(formChangesCopy, fieldName);
                    _.set(formChangesResult, fieldName, newValue);
                    applyChange = true;
                }
            }
        }

        if (applyChange) {
            result.applyChanges = true;
            result.formChanges = _.cloneDeep(formChangesCopy);
            result.formChangesResult = _.cloneDeep(formChangesResult);
        }

        // console.log(`${msgPrefix} return fieldName ${fieldName} newValue ${newValue}`, { result });

        return result;
    }

    // DEPRECATED:
    public static buildSelectOptions(options: Array<any>, sortByField: string, labelField: string, valueField: string): Array<any> {
        // DEPRECATED: REFACTOR CALLERS TO USE FormUtilities.prepareSelectOptions instead
        // Return an array of <Select> options
        let results: Array<any> = [];

        Errors.ensureRequiredArgument(FormUtilities.ModuleName, 'buildSelectOptions', 'options', options);
        Errors.ensureRequiredArgument(FormUtilities.ModuleName, 'buildSelectOptions', 'labelField', labelField);
        Errors.ensureRequiredArgument(FormUtilities.ModuleName, 'buildSelectOptions', 'valueField', valueField);

        if (AppUtilities.isArrayWithLength(options)) {
            // Sort records, build select options
            let sorted = sortByField ? _.sortBy(options, [sortByField]) : options;

            results = sorted.map((c: any) => {
                return FormUtilities.buildSelectOption(c[labelField], c[valueField]);
            });
        }

        return results;
    }

    public static buildSubgroupSelectOptions(options: Array<any>, sortByField: string, labelField: string, valueField: string): Array<any> {
        // Return an array of <Select> options
        let results: Array<any> = [];

        Errors.ensureRequiredArgument(FormUtilities.ModuleName, 'buildSelectOptions', 'options', options);
        Errors.ensureRequiredArgument(FormUtilities.ModuleName, 'buildSelectOptions', 'labelField', labelField);
        Errors.ensureRequiredArgument(FormUtilities.ModuleName, 'buildSelectOptions', 'valueField', valueField);

        if (AppUtilities.isArrayWithLength(options)) {
            // Sort records, build select options
            let sorted = sortByField ? _.sortBy(options, (inv) => _.isFinite(_.parseInt(inv.groupCode)) ? _.parseInt(inv.groupCode) : inv.groupCode) : options;
            // let sorted = sortByField ? _.sortBy(options, [sortByField]) : options;

            results = sorted.map((c: any) => {
                return FormUtilities.buildSelectOption(c[labelField], c[valueField]);
            });
        }

        return results;
    }

    public static padSubgroup(invItem: any): any {
        let result = invItem;
        let existingGroup = result.subgroup;
        let modifiedGroup = _.padStart(existingGroup, ((_.startsWith(existingGroup, '0')) ? _.size(existingGroup) : _.size(existingGroup) + 1), '0');
        _.set(result, 'subgroup', modifiedGroup);
        return result;
    }

    public static padSubgroupLabel(invItem: any): any {
        let result = invItem;
        let existingLabel = result.subgroupLabel;
        let modifiedLabel = _.padStart(existingLabel, ((_.startsWith(existingLabel, '0')) ? _.size(existingLabel) : _.size(existingLabel) + 1), '0');
        _.set(result, 'subgroupLabel', modifiedLabel);
        return result;
    }

    public static prepareSelectOptions(options: Array<any>, valueField: string, sortByField: string = 'label', labelField: string = 'label', disabledField: string = 'disabled'): Array<any> {
        // Return an array of <Select> options
        let results: Array<any> = [];

        Errors.ensureRequiredArgument(FormUtilities.ModuleName, 'prepareSelectOptions', 'options', options);
        Errors.ensureRequiredArgument(FormUtilities.ModuleName, 'prepareSelectOptions', 'labelField', labelField);
        Errors.ensureRequiredArgument(FormUtilities.ModuleName, 'prepareSelectOptions', 'valueField', valueField);

        if (AppUtilities.isArrayWithLength(options)) {
            // Sort records, build select options
            let sorted = sortByField ? _.sortBy(options, [sortByField]) : options;

            results = sorted.map((c: any) => {
                return FormUtilities.buildSelectOption(_.get(c, labelField), _.get(c, valueField), _.get(c, disabledField));
            });
        }

        return results;
    }

    public static buildSelectOption(labelText: string, value: any, disabled: boolean = false, labelField: string = 'label', valueField: string = 'value') {
        // Returns a single option object for use in <Select> options array
        let init = {
            disabled: disabled
        };

        init = _.set(init, labelField, labelText);
        init = _.set(init, valueField, value);

        return _.cloneDeep(init);
    }

    public static addAnyOption(records: Array<any>, partialObject: any, labelText: string, labelField: string = 'label') {
        // Add an 'any' option to an array of values with label value of `labelText`
        let options = _.cloneDeep(records);

        let anyOption = {
            disabled: false,
        };

        anyOption = _.set(anyOption, labelField, labelText);

        options.push(Object.assign({}, partialObject, anyOption));

        return options;
    }

    public static addDefaultDisabledOption(records: Array<any>, partialObject: any, labelText: string, labelField: string = 'label') {
        // Add a default disabled option to an array of values with label value of `labelText`
        let options = _.cloneDeep(records);

        let disabledOption = {
            disabled: true,
        };

        disabledOption = _.set(disabledOption, labelField, labelText);

        options.push(Object.assign({}, partialObject, disabledOption));

        return options;
    }

    public static setLabelValues(options: Array<any>): Array<any> {
        // Returns array of options with 'label' field value set on each
        if (!AppUtilities.isArrayWithLength(options)) { return []; }

        return options.map(record => {
            if (!record.label) {
                record.label = record.name;
            }

            return record;
        });
    }

    public static getDatePickerSelectedValue(date: string) {
        // Returns a moment date, used by DatePicker
        if (!date) { return null; }

        return moment(date, AppConfiguration.getMomentShortDateFormat());
    }

    public static extendFilterData(records: Array<any>, valueField: string, sortField: string, anyOptionText: string, anyOptionValue: any = -1, anyOptionSortValue: any = -1): Array<any> {
        // Add dynamic fields to client records
        if (!records) { return []; }

        let partial = _.set({ name: '' }, valueField, anyOptionValue);
        partial = _.set(partial, sortField, anyOptionSortValue);
        let options = FormUtilities.addAnyOption(records, partial, anyOptionText);

        return FormUtilities.setLabelValues(options);
    }

    public static getExtendedFilterOptions(records: Array<any>, valueField: string, sortField: string, anyOptionText: string, anyOptionValue: any = -1, anyOptionSortValue: any = -1) {
        // Extend filter data records with label
        let result = new SelectOptionsConfig();

        if (!records) { return result; }

        result.extended = FormUtilities.extendFilterData(records, valueField, sortField, anyOptionText, anyOptionValue, anyOptionSortValue);
        result.options = FormUtilities.prepareSelectOptions(result.extended, valueField, sortField);
        result.filter = createFilterOptions({ options: result.options });

        return result;
    }

    public static getExtendedFilterOptionsMulti(records: Array<any>, valueField: string, sortField: string, anyOptionText: string, anyOptionValue: any = -1, anyOptionSortValue: any = -1) {
        // Extend filter data records with label
        let result = new SelectOptionsConfig();

        if (!records) { return result; }

        result.extended = FormUtilities.extendFilterData(records, valueField, sortField, anyOptionText, anyOptionValue, anyOptionSortValue);
        result.options = FormUtilities.prepareSelectOptions(result.extended, valueField, sortField);
        result.filter = createFilterOptions({ options: result.options });

        return result;
    }
}



// WEBPACK FOOTER //
// ./src/common/formutilities.tsx